import { useCallback } from "react";
import { getAnonId, getHandle } from "./identity.js";

/**
 * Crash game hook. start() opens a server round (returns the round + its committed crash
 * point for the climb animation); cashout(round, mult) resolves it server-side. The
 * server re-derives the crash point and bounds the awarded multiplier by its own clock,
 * so the result is honest regardless of the client (see /api/crash/* in game-api).
 *
 * The round is passed back into cashout() by the caller (held in the app store) rather
 * than kept on the hook — start and cashout may be called from different component
 * instances, so a hook-internal ref wouldn't carry over.
 */

export interface CrashRound {
  roundId: string;
  startedAt: number;
  sig: string;
  /** The round's crash point (for the animation only — server re-derives at cashout). */
  crashPoint: number;
  /** Per-second growth rate of the climb. */
  growth: number;
}

export interface CrashResult {
  won: boolean;
  /** Cashed-out multiplier (win only). */
  multiplier?: number;
  /** The real crash point, revealed on resolution. */
  crashPoint: number;
  value?: number;
  rank?: number;
  total?: number;
  percentile?: number;
}

export function useCrash(game: string) {
  const start = useCallback(async (): Promise<CrashRound | null> => {
    const res = await fetch("/api/crash/start", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ game, anonId: getAnonId() }),
    });
    if (!res.ok) return null;
    return (await res.json()) as CrashRound;
  }, [game]);

  /** Cash out the given round at the displayed multiplier. Returns null if no handle
   *  (caller collects one) or on a network/server error. */
  const cashout = useCallback(
    async (
      round: CrashRound,
      cashoutMultiplier: number,
    ): Promise<CrashResult | null> => {
      const handle = getHandle();
      if (handle === null) return null;
      const res = await fetch("/api/crash/cashout", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          game,
          anonId: getAnonId(),
          handle,
          roundId: round.roundId,
          startedAt: round.startedAt,
          sig: round.sig,
          cashoutMultiplier,
        }),
      });
      if (!res.ok) return null;
      return (await res.json()) as CrashResult;
    },
    [game],
  );

  return { start, cashout };
}
